import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from '../../../../services/message-service/message.service';
import { GruppeTier } from '../../classes/gruppe-tier';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { GruppeTierService } from '../../gruppe-tier.service';
import { GruppenService } from '../../../gruppen/gruppen.service';
import { BetriebskontakteService } from '../../../stammdaten/betriebskontakte/betriebskontakte.service';
import { Betriebskontakt } from '../../../stammdaten/betriebskontakte/betriebskontakt';

@Component({
    selector: 'kiene-gruppe-tier-betriebskontakt-haendler-zuordnen',
    templateUrl: './gruppe-tier-betriebskontakt-haendler-zuordnen.component.html',
    styleUrls: ['./gruppe-tier-betriebskontakt-haendler-zuordnen.component.scss'],
})
export class GruppeTierBetriebskontaktHaendlerZuordnenComponent implements OnInit, OnDestroy {
    @Input('dialog') dialog = false;
    @Input('tier') tier: GruppeTier;
    @Output('saved') saved: EventEmitter<GruppeTier> = new EventEmitter();

    isLoading = false;
    tierSubscription: Subscription;
    buttonText = 'Speichern';
    private saveTimeout: any;

    // Betriebskontakt-Händler Daten
    betriebskontaktHaendler: Betriebskontakt | null = null;
    betriebskontaktHaendlerLoading = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private gruppenService: GruppenService,
        private gruppeTierService: GruppeTierService,
        private messageService: MessageService,
        private matDialog: MatDialog,
        private betriebskontakteService: BetriebskontakteService,
    ) {}

    ngOnDestroy(): void {
        this.tierSubscription?.unsubscribe();
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }
    }

    ngOnInit(): void {
        if (this.tier) {
            this.loadBetriebskontaktHaendler();
        } else {
            this.route.params.subscribe(params => {
                this.loadTier(params['gt_id']);
            });
        }
    }

    loadTier(tierId: string) {
        this.tierSubscription?.unsubscribe();
        this.tierSubscription = this.gruppenService.loadTier(tierId).subscribe({
            next: (tier: GruppeTier) => {
                this.tier = tier;
                this.loadBetriebskontaktHaendler();
            },
            error: err => {
                this.messageService.errorMessage('Fehler beim Laden des Tieres', err);
            },
        });
    }

    loadBetriebskontaktHaendler() {
        this.betriebskontaktHaendlerLoading = false; // Setze Loading auf false, da wir keine API-Abfrage machen

        if (this.tier?.betriebskontakt_haendler_id && this.tier?.betriebskontakt_haendler) {
            // Erstelle ein Betriebskontakt-Objekt aus den bereits vorhandenen Daten
            this.betriebskontaktHaendler = {
                kunde_id: this.tier.betriebskontakt_haendler_id,
                bezeichnung: this.tier.betriebskontakt_haendler,
                bezeichnung_final: this.tier.betriebskontakt_haendler,
                vvvo: this.tier.vvvo_betriebskontakt_haendler || '',
                zusatz: '',
                zusatz_final: '',
                strasse: '',
                plz: '',
                ort: '',
                betriebsart_bezeichnung: '',
                kontakt_id: 0,
                benutzer_id: 0,
                favorit: 0,
                ist_kunde: 0,
                erstellt: new Date().toISOString(),
                erstellt_von: 0,
                editiert: new Date().toISOString(),
                editiert_von: 0,
            } as Betriebskontakt;
            this.buttonText = 'Aktualisieren';
        } else {
            this.betriebskontaktHaendler = null;
            this.buttonText = 'Speichern';
        }
    }

    save() {
        // Verhindere mehrfache Aufrufe während ein Request läuft
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        // Verwende die neue schnelle API nur für Händler-Updates
        this.gruppeTierService.updateHaendler(this.tier).subscribe({
            next: (tier: GruppeTier) => {
                this.isLoading = false;
                this.tier = tier;
                this.messageService.infoMessage('Kontakt wurde gespeichert');
                if (this.dialog) {
                    // Dialog automatisch schließen nach erfolgreichem Speichern
                    this.saved.emit(tier);
                }
            },
            error: err => {
                this.isLoading = false;
                this.messageService.errorMessage('Fehler beim Speichern des Kontakts', err);
            },
        });
    }

    onBetriebskontaktSelected(kontakt: Betriebskontakt): void {
        if (kontakt) {
            this.betriebskontaktHaendler = kontakt;
            this.tier.betriebskontakt_haendler_id = kontakt.kunde_id;
            this.buttonText = 'Speichern';

            // Debounced speichern - verhindert zu viele API-Aufrufe bei schnellen Änderungen
            if (this.saveTimeout) {
                clearTimeout(this.saveTimeout);
            }
            this.saveTimeout = setTimeout(() => {
                this.save();
            }, 500); // 500ms Verzögerung
        }
    }

    onBetriebskontakteSelected(kontakte: Betriebskontakt[]): void {
        if (kontakte && kontakte.length > 0) {
            this.onBetriebskontaktSelected(kontakte[0]);
        }
    }

    onBetriebskontaktEntfernen(): void {
        this.betriebskontaktHaendler = null;
        this.tier.betriebskontakt_haendler_id = null;
        this.buttonText = 'Speichern';
    }

    getVollAdresse(kontakt: Betriebskontakt): string {
        const parts = [];
        if (kontakt.strasse_final) parts.push(kontakt.strasse_final);
        if (kontakt.plz_final) parts.push(kontakt.plz_final);
        if (kontakt.ort_final) parts.push(kontakt.ort_final);
        return parts.join(' ');
    }

    getTelefonNummer(kontakt: Betriebskontakt): string | null {
        return kontakt.telefon_1 || kontakt.mobil_1 || kontakt.telefon_2 || kontakt.mobil_2 || null;
    }
}
